/**
 * App-local English catalogue for `order.` - the guest storefront.
 *
 * The split is deliberate (PRD NFR-006). Strings that must read identically on
 * every surface - the GST-036 stage model, notification copy, receipts - live in
 * `@croissants/i18n` and are imported from there. What lives here is screen copy
 * that only this app renders, so no other frontend has to merge against it.
 *
 * English is the key set of record: `fr.ts` is typed against this file, so a
 * missing or misspelt key is a compile error rather than a silent English string
 * shown to a guest at 06:30.
 */

export const en = {
  // -------------------------------------------------------------------------
  // Shell and navigation
  // -------------------------------------------------------------------------
  'app.skipToContent': 'Skip to content',
  /* The shell's own controls (packages/ui `AppShell`). They are accessible names
     only - the glyphs carry no text - and each pair changes with the state it
     describes, so a screen reader hears what the control will DO next. */
  'app.openMenu': 'Open menu',
  'app.closeMenu': 'Close menu',
  'app.collapseSidebar': 'Collapse sidebar',
  'app.expandSidebar': 'Expand sidebar',
  'app.loading': 'Loading',
  'app.mainLabel': 'Main content',
  'nav.menu': 'Menu',
  'nav.basket': 'Basket',
  'nav.orders': 'My orders',
  'nav.label': 'Sections',
  'nav.basketCount': {
    one: 'Basket, {count} item',
    other: 'Basket, {count} items',
  },

  // -------------------------------------------------------------------------
  // Language switch (PRD GST-006)
  // -------------------------------------------------------------------------
  'language.label': 'Language',
  // Endonyms: a language is offered under its own name, spelled the way that
  // language spells it, and identically in every catalogue (NFR-006).
  'language.en': 'English',
  'language.fr': 'Français',
  // What a tap on the flag does, each written in the language it LEADS to and
  // identically in every catalogue, like the endonyms above: the reader who
  // needs "Passer en francais" is the one who cannot read the page they are on.
  'language.switchToEn': 'Switch to English',
  'language.switchToFr': 'Passer en français',

  // -------------------------------------------------------------------------
  // How to reach us (PRD GST-043)
  // -------------------------------------------------------------------------
  'support.title': 'Contact us',
  'support.whatsapp': 'WhatsApp',
  'support.call': 'Call {number}',
  'support.mail.writeAbove': 'Please write your message above this line.',
  'support.mail.apartment': 'Apartment',
  'support.mail.address': 'Address',
  'support.mail.orders': 'Orders',
  'support.mail.language': 'Language',
  'support.mail.device': 'Device',
  'support.mail.subject': 'Croissant in Bed support',
  'support.mail.subjectForApartment': 'Croissant in Bed support - {apartment}',

  // -------------------------------------------------------------------------
  // QR landing (PRD GST-001, GST-028)
  // -------------------------------------------------------------------------
  'landing.resolving': 'Opening your apartment storefront',
  'landing.noToken.title': 'Scan the code in your apartment',
  'landing.noToken.body':
    'This storefront opens from the QR code your host left in the apartment. Scan it with your phone camera to start.',
  'landing.invalid.title': 'This code did not open',
  'landing.invalid.body':
    'The code may have been replaced. Ask your host for the current one, or contact us.',
  // The body promises a contact route, so the screen must offer one (GST-040).
  'landing.invalid.contactAction': 'Contact us',
  // Not the same failure as an invalid code, and not the same sentence: the code
  // is probably fine, we simply could not reach the service to check it.
  'landing.unreachable.title': 'We could not open the storefront',
  'landing.deliveringTo': 'Delivering to {place}',

  // -------------------------------------------------------------------------
  // Storefront (PRD GST-003, GST-029 to GST-032)
  // -------------------------------------------------------------------------
  'menu.photoPosition': 'Photo {position} of {count}',
  'menu.empty': 'Nothing is on the counter for this morning yet.',
  'menu.openLabel': 'See {name}',
  'menu.addQuantity': 'Add {count} to basket \u00b7 {amount}',
  'menu.addLabel': 'Add {name}',
  'menu.inBasket': {
    one: '{count} in basket',
    other: '{count} in basket',
  },
  'menu.dietary': 'Suitable for',
  'menu.maxPerOrder': 'Up to {count} per order',
  'menu.formule.included': 'Included',
  'menu.formule.required': 'Required',
  'menu.viewBasket': 'View basket',
  /** Same bar as `menu.viewBasket`, before a morning has been chosen (GST-030). */
  'menu.setDeliveryTime': 'Set delivery time',
  /** Mode B2's bar before a time is chosen: it opens the delivery popup (GST-045). */
  'menu.scheduleDelivery': 'Schedule this delivery',
  /* The arrival promise on an order for today (PRD GST-042, RTE-034). The
     range beside it is the window that was chosen; this says which product
     it is - the soonest one the platform can reach, not a morning. */
  'menu.arrivingToday': 'Today, as soon as possible',
  'menu.allergen.GLUTEN': 'Gluten',
  'menu.allergen.CRUSTACEANS': 'Crustaceans',
  'menu.allergen.EGGS': 'Eggs',
  'menu.allergen.FISH': 'Fish',
  'menu.allergen.PEANUTS': 'Peanuts',
  'menu.allergen.SOYBEANS': 'Soy',
  'menu.allergen.MILK': 'Milk',
  'menu.allergen.NUTS': 'Nuts',
  'menu.allergen.CELERY': 'Celery',
  'menu.allergen.MUSTARD': 'Mustard',
  'menu.allergen.SESAME': 'Sesame',
  'menu.allergen.SULPHITES': 'Sulfites',
  'menu.allergen.LUPIN': 'Lupin',
  'menu.allergen.MOLLUSCS': 'Molluscs',
  'menu.crossContamination': 'May contain traces of any of the 14 major allergens.',
  'menu.dietary.VEGETARIAN': 'Vegetarian',
  'menu.dietary.VEGAN': 'Vegan',
  'menu.dietary.GLUTEN_FREE': 'Gluten free',
  'menu.dietary.LACTOSE_FREE': 'Lactose free',
  'menu.dietary.HALAL': 'Halal',

  // -------------------------------------------------------------------------
  // Shop chooser, mode B (PRD GST-030 to GST-032)
  // -------------------------------------------------------------------------
  'shops.title': 'Choose your bakery',
  // The morning is no longer the next step - it is chosen from the basket bar
  // once the guest has seen the counter (App.tsx `Home`), so this no longer
  // names it.
  'shops.subtitle': 'These houses can deliver to your address.',
  'shops.empty': 'No bakery can serve this address on that morning yet.',
  'shops.select': 'See the counter',
  'shops.everyDay': 'Every day',
  'shops.closedOn': 'closed {days}',
  'shops.deliveryToday': 'Delivery today {times}',
  'shops.deliveryTomorrow': 'Delivery tomorrow {times}',
  'shops.deliveryLater': 'Delivery {day} {times}',
  'shops.deliveryUntil': 'until {time}',
  'shops.closedUntil': 'Bakery closed, reopens {date}',
  'shops.change': 'Change bakery',
  'shops.changeConfirmTitle': 'Change bakery?',
  'shops.changeConfirmAction': 'Change and empty the basket',

  // -------------------------------------------------------------------------
  // Mode B2: the delivery popup and the line under the address (PRD GST-045)
  // -------------------------------------------------------------------------
  // The popup over the shelf: a title, the two sections, and - while the
  // bakery is shut - the one sentence saying so and when it opens again. The
  // baker is never named, so the copy says "your baker".
  'delivery.title': 'Choose your delivery',
  'delivery.close': 'Close',
  'delivery.now': 'Order now',
  'delivery.later': 'Schedule for later',
  // Under "Order now", and under the address once it is chosen. A figure and
  // never a range: the range is the quote's, on the review screen (RTE-034).
  // No "at earliest" (founder, 2026-09-21). `delivery.in` is the same line
  // while the figure is still loading - the words, then a loading wheel.
  'delivery.in': 'Delivery in',
  'delivery.inMinutes': 'Delivery in about {minutes} min',
  // The same figure under "Delivering to ...": the line above already says
  // delivery, so this one does not say it again (founder, 2026-09-21).
  'delivery.lineInMinutes': 'In about {minutes} min',
  // Leads the week under the schedule section, printed as the shop tile does.
  'delivery.bakerOpen': 'Your baker is open',
  'delivery.closed': 'Your baker is currently closed',
  // Under that sentence, which stands alone as the popup's heading.
  'delivery.opensToday': 'Opens today at {time}',
  'delivery.opensTomorrow': 'Opens tomorrow at {time}',
  'delivery.opensLater': 'Opens {day} at {time}',
  // The third line of the band under the brand bar: what the guest chose.
  // "Today, 12:45-13:15" / "22-24, 26 Sept, 07:15-07:45": the day is a name, a
  // date, or several mornings in the least space (`compactDays`).
  'delivery.chosen': '{day}, {window}',

  // -------------------------------------------------------------------------
  // Basket (PRD GST-026, GST-032)
  // -------------------------------------------------------------------------
  'basket.title': 'Your basket',
  'basket.empty': 'Your basket is empty',
  'basket.emptyBody': 'Add a menu or an extra and it will show up here.',
  'basket.browse': 'See the breakfast counter',
  'basket.increase': 'One more {name}',
  'basket.decrease': 'One fewer {name}',
  'basket.remove': 'Remove {name}',
  'basket.quantity': 'Quantity of {name}',
  'basket.subtotal': 'Items',
  // The morning was chosen before any of this; what is left is the review.
  'basket.continue': 'Review and pay',
  'basket.fulfilledBy': 'From {shop}',
  /* A basket outlives the morning it was filled for, and the counter serving an
     address changes with the morning (GST-029). The line is kept, named and
     flagged rather than silently priced at nothing: the guest chose it, and only
     they can decide to drop it (GST-021, GST-026). */
  'basket.lineUnavailable': 'Not served this morning',
  'basket.removeShort': 'Remove',
  'basket.unavailableTitle': 'Some items are not served this morning',
  'basket.unavailableBody':
    'What is on the counter changes from one morning to the next, and these are not on it. Remove them and pick something else - the rest of your basket is untouched.',
  'basket.removeUnavailable': 'Remove those items',
  // GST-163: the guest's note to the house, on the basket.
  'basket.orderNote': 'Order note',
  'basket.orderNoteAdd': 'Add an order note',
  'basket.orderNotePlaceholder': 'E.g. please slice the baguette, no napkins',

  // -------------------------------------------------------------------------
  // Window picker (PRD GST-002) - the first step in mode A, the step after
  // the brand's menu in mode B (GST-030)
  // -------------------------------------------------------------------------
  'windows.selected': 'Arriving {window}',
  'windows.none': 'No window is open for this morning.',
  /* The name of the half-hour group, and the visible line over it. It names
     the SELECTION rather than a morning: the list is what every selected
     morning can serve, so naming the first of them would say the times
     belonged to one day out of several (founder decision, 2026-08-24). */
  'windows.windowLabel': 'Delivery windows available for your day selection',
  /* Nothing is selected yet, so there is nothing to list. */
  'windows.pickADay': 'Pick a morning above to see the times it can be delivered.',
  /* Several mornings are selected and no one time serves them all - the list
     is their intersection, and it is empty. The way out is a morning off. */
  'windows.noneForSelection':
    'No single time works for every morning you picked. Unselect one to see more times.',
  /* The top line of a day card, on the first two cards only. The rest of the
     strip is a formatted weekday, so only these two are words - and they are
     the two a guest reads without doing arithmetic against a date. */
  'windows.today': 'Today',
  'windows.tomorrow': 'Tomorrow',
  /* The picker's own title once a morning is selected (founder decision,
     2026-08-24). The count used to be a muted line of its own above the strip
     - "2 mornings selected" - under a heading that already read "Order for",
     which is the same fact said twice. With nothing selected the title is the
     bare `windows.orderFor` below. */
  'windows.orderForDays': {
    one: 'Order for 1 day',
    other: 'Order for {count} days',
  },
  'windows.confirm': 'Schedule this delivery',
  /* The same button when the anchor is today (RTE-034): nothing is being
     scheduled, so the word for it is not the scheduling one. */
  'windows.confirmNow': 'Deliver now',
  /* The ASAP window - the first one today's accept, prepare, travel and
     margin arithmetic can still reach. Tagged, and chosen by default. */
  'windows.soonest': 'Soonest',
  /* Heard, not seen: the state of a grey card, for a reader who cannot see
     that it is faded. */
  'windows.blocked.unavailable': 'Not available',
  /* Beside the window label when the list is today's: the instant it was
     computed for, so a guest returning to a stale tab sees why it moved. */
  'windows.nowIs': 'it is {time}',
  'windows.change': 'Change',
  /** Labels the chosen-mornings list; one row follows per morning of the stay. */
  'windows.orderFor': 'Order for',
  /* The window stopped fitting between the picker and the price (RTE-008): a
     bigger basket takes longer to make, another guest took the slot, the cutoff
     passed. Not an error to retry - a choice to make again. */
  'windows.conflictTitle': 'That time no longer fits',
  'windows.conflictBody':
    'Your basket needs more preparation than the window you picked still allows, so it cannot arrive then. These are the nearest times that work.',
  'windows.alternative': '{day}, {window}',
  'windows.conflictAction': 'See all times',

  // -------------------------------------------------------------------------
  // Checkout (PRD GST-006, GST-021 to GST-026)
  // -------------------------------------------------------------------------
  'checkout.step.details': 'Your details',
  'checkout.step.review': 'Review',
  'checkout.step.pay': 'Payment',
  'checkout.stepOf': 'Step {step} of {total}',
  // NTF-009: the opt-in beside "leave it at the door". Transactional, not
  // marketing (NTF-008): updates about the order being placed, nothing else.
  'checkout.whatsappUpdates': 'Order updates on WhatsApp',
  'checkout.required': '(required)',
  'checkout.optional': '(optional)',
  'checkout.items': 'Your order',
  /* Last resort for a line the catalogue could not name and that was added
     before this browser remembered names (GST-021). It says what happened
     instead of printing an id the guest cannot check against anything. */
  'checkout.itemUnnamed': 'Item (name unavailable)',
  /* The window when only its start is known - the quote has not answered and the
     picker's list is not loaded. Phrased as an opening time, never as a range we
     have not been told (GST-002). */
  'checkout.windowFrom': 'from {time}',
  'checkout.editItems': 'Change items',
  /* Said instead of the server's answer to a basket it cannot price. That answer
     is "no bakery can serve this address", which is about the flat and is not
     what went wrong (GST-021). */
  'checkout.itemsUnavailable':
    'Your order still holds items that are not served on the morning you chose, so it cannot be priced. Change your items to carry on.',
  'checkout.editDetails': 'Change your details',
  'checkout.correctDetails': 'Correct or add anything the courier needs',
  /* The one line of prose in the delivery sheet. It says what the sheet is FOR
     rather than restating an order-scoping rule nobody was asking about
     (founder decision 2026-08-24). */
  'checkout.correctDetailsHint': 'Modify or add additional instructions for the courier',
  /* The door-position options, in the partner portal's own words - it is one
     closed set describing one door (`prop.door.*`). */
  'checkout.door.LEFT': 'Left',
  'checkout.door.RIGHT': 'Right',
  'checkout.door.CENTRE': 'Center',
  'checkout.door.FACING': 'Facing the stairs',
  'checkout.legal.terms': 'Terms and conditions',
  'checkout.legal.howItWorks': 'How it works',
  'checkout.legal.privacy': 'Privacy policy',
  'checkout.legal.legal': 'Legal notice',
  'checkout.legal.intro': 'Before you pay',
  'footer.legalLabel': 'Legal information',
  'checkout.stayTotal': 'Total for your stay',
  /* The count line above the stay's list of mornings. It describes THAT list -
     `stay.mornings`, day one included - so it is a plain count and never "more".
     It borrowed `schedule.mornings` ("{count} more mornings") until this key
     existed, and read "3 more mornings" over a list of exactly three whose first
     row was tomorrow: a fourth morning the guest is not being charged for. */
  'checkout.stayMornings': {
    one: '{count} morning',
    other: '{count} mornings',
  },
  'checkout.morningUnavailable': 'Not available',
  /* The reason line on a stay row nobody can serve (multi-day plan 2.3):
     "full" invites picking another window; "closed" does not. */
  'checkout.stayReason.BAKERY_CLOSED': 'Bakery closed',
  'checkout.stayReason.NO_CAPACITY': 'Fully booked',
  'checkout.stayReason.NO_BAKERY_AVAILABLE': 'No bakery can deliver',
  'checkout.stayReason.WRITE_FAILED': 'Could not be placed',
  'checkout.goToPayment': 'Go to payment',
  /* Under two half-width fields on a 375px payment sheet, so as short as they
     can be and still say what to do (ruling #24: the error state must fit). */
  'checkout.validation.name': 'Enter a name.',
  'checkout.validation.phone': 'Mobile with country code.',
  'checkout.validation.email': 'That email address does not look right.',
  'checkout.validation.window': 'Please choose a morning and a delivery window.',
  'checkout.validation.basket': 'Your basket is empty.',

  // -------------------------------------------------------------------------
  // Payment (PRD GST-005, PAY-002)
  // -------------------------------------------------------------------------
  'checkout.itemCount': {
    one: '{count} item',
    other: '{count} items',
  },
  'checkout.instructionsAdd': 'Add instructions',
  // The review's first block: where it goes and how to get in, under ONE title
  // (founder, 2026-09-21).
  // SHORT ON PURPOSE (founder, 2026-09-21): it shares its line with the chevron
  // and must fit a 320px phone, which holds about 27 characters of this type.
  // Measured: "Address and courier instructions" still clipped at 360px.
  'checkout.addressAndInstructions': 'Address and instructions',
  // How to reach the flat, in the words a person would say at the door -
  // "Building B, 3rd floor, left door". Never the access code (GST-024).
  'checkout.brief.building': 'building {name}',
  'checkout.brief.floor': 'floor {label}',
  'checkout.brief.groundFloor': 'ground floor',
  'checkout.brief.doorPosition': '{position} door',
  'checkout.brief.doorNumber': 'door {number}',
  'checkout.brief.intercom': 'intercom {name}',
  'checkout.brief.lift': 'lift',
  'checkout.brief.noLift': 'no lift',
  // WHEN, as one statement: the mornings themselves are under the address at
  // the top of the screen, so the review says how many, or the courier's ETA.
  'checkout.scheduledFor': 'Scheduled for',
  'checkout.scheduledDays': { one: '1 day', other: '{count} days' },
  'checkout.estimatedArrival': 'Estimated arrival',
  'checkout.accessCodeSet': 'Code saved',
  'checkout.addressAdd': 'Add floor, door, intercom',
  'payment.orByCard': 'or pay by card',
  'payment.walletNoContact':
    'That wallet did not give us a name and a mobile number for the courier. Please pay by card.',
  'payment.title': 'Payment',
  'payment.summary': 'You are paying {amount}',
  'payment.wallets': 'Apple Pay, Google Pay and cards are accepted.',
  'payment.neverStored': 'Card details go straight to our payment provider. We never see them.',
  'payment.confirm': 'Pay {amount}',
  'payment.processing': 'Confirming your payment',
  'payment.failed': 'The payment was not completed.',
  'payment.testMode.title': 'Test mode',
  'payment.testMode.body':
    'No payment provider key is configured for this environment, so the order is authorized with the platform test method. No card is charged.',
  'payment.testMode.action': 'Place the test order',
  'payment.unavailable': 'The payment form could not be loaded. Please try again.',

  // -------------------------------------------------------------------------
  // Confirmation
  // -------------------------------------------------------------------------
  'confirm.title': 'Your breakfast is on its way to being made',
  'confirm.body': 'Order {code}. We will keep this page updated as it moves.',
  'confirm.track': 'Track this order',
  'confirm.scheduleCreated': 'We also planned the other mornings of your stay.',
  // One line per morning checkout could not place (F-24): the day and why.
  'confirm.skipped.BAKERY_CLOSED': '{day}: the bakery is closed',
  'confirm.skipped.NO_CAPACITY': '{day}: no delivery slot left at that time',
  'confirm.skipped.NO_BAKERY_AVAILABLE': '{day}: no bakery can deliver',
  'confirm.skipped.WRITE_FAILED': '{day}: could not be placed',

  // -------------------------------------------------------------------------
  // Order detail (PRD GST-008, GST-035, GST-039)
  // -------------------------------------------------------------------------
  'order.courierTitle': 'Delivery',
  // A line the counter replaced: what it replaced, on the line beside the
  // "Sold out · Replaced" pill (#2004, approved 2026-10-08, P222).
  'order.line.insteadOf': 'instead of {name}',
  'order.courierUnnamed': 'Your courier',
  'order.courierLocation': 'Live position',
  'order.courierLocationAt': 'Updated {time}',
  'order.trackWithProvider': 'Open live tracking',
  /* The four labels the live map needs and nothing more (GST-035): a map is not
     a screen that gets a caption, so these are the pins' accessible names and
     the canvas's own, read by anybody who cannot see it. */
  'order.map.label': 'Where your courier is',
  'order.map.shop': 'Pickup point',
  'order.map.home': 'Your address',
  'order.map.courier': 'Courier',
  /* The courier's vehicle (GST-035). The provider sends its own word for this -
     `bicycle`, `walker` - and a provider's vocabulary is not copy: NFR-006 has
     every user-facing string come from here, so the panel resolves the token
     against these and omits the line when it recognises none of them. */
  'courier.vehicleType.WALKER': 'On foot',
  'courier.vehicleType.BICYCLE': 'Bicycle',
  'courier.vehicleType.SCOOTER': 'Scooter',
  'courier.vehicleType.MOTORCYCLE': 'Motorcycle',
  'courier.vehicleType.CAR': 'Car',
  'courier.vehicleType.VAN': 'Van',
  'courier.vehicleType.TRUCK': 'Truck',
  'order.deliveryDetails': 'Delivery address',
  'order.placedAt': 'Placed {time}',
  'order.summaryTitle': 'Your order',
  'order.reviewSubmitted': 'Everything you submitted',
  'order.notFound': 'Order not found for this code.',
  'order.error.accessBody': 'The link may be old. Check your orders.',
  'order.error.linkTitle': 'Incomplete link',
  'order.error.linkBody': 'Check your orders instead.',
  'order.error.unreachableTitle': 'Connection problem',
  'order.backToOrders': 'Back to your orders',
  'order.backToStorefront': 'Back to the storefront',
  /* The dialog's second line: the figure, and nothing about when. "back in a
     few days" named a settlement date the platform does not decide, on a
     movement that now waits for an operator (PAY-030). */
  'order.refundNote': 'Refund of {amount}.',
  'order.reportIssue': 'Report a problem',
  /* The guest's two invoices (invoicing.html#delivery): the button beside the
     receipt, the popup its press opens, what it reads once the Baker's invoice
     exists, and the product invoice still owed. */
  'order.requestInvoice': 'Request an invoice',
  'order.invoiceRequested': 'Your invoice will be available shortly here and sent to you by email',
  'order.invoiceRequestedOk': 'OK',
  /* Dismisses the message an order that ended without delivery carries on the
     orders list and its page (#2004). */
  'order.ending.ok': 'OK',
  'order.invoice': 'Invoice',
  'order.productsInvoicePending': 'Products invoice on its way',
  // Beside a document in that list: a credit note, and an invoice one cancelled.
  'order.invoiceTag.creditNote': 'Credit note',
  'order.invoiceTag.canceled': 'Canceled',
  'order.substitutionChoice': 'Replacement if an item is unavailable',
  /* The block above the lines: one title, the missing items, one money
     sentence - short and plain (founder, 2026-10-01). */
  'order.soldOut.title': 'Sold out at your bakery',
  'order.soldOut.refund': 'The difference will be refunded.',
  'order.deliveredAt': 'Delivered {time}',
  /* The one action beside OK when no bakery could take the order (ALC-006). */
  'order.unfulfilled.action': 'Order for tomorrow',

  // -------------------------------------------------------------------------
  // Tip and ratings (PRD GST-037, GST-038)
  // -------------------------------------------------------------------------
  'tip.amountLabel': 'Tip amount',
  'tip.customLabel': 'In euros',
  'tip.unavailable': 'Tipping is not available for this delivery.',
  'rating.starLabel': '{count} out of 5',
  'rating.deliveryHelp': 'Courier, timing, handover.',

  // -------------------------------------------------------------------------
  // Issue reporting (PRD GST-039)
  // -------------------------------------------------------------------------
  'issue.photoLabel': 'Add a photo',
  // The two sources a phone actually has, named as the buttons that open them.
  'issue.photoCamera': 'Camera',
  'issue.photoLibrary': 'Photo library',
  'issue.photoPreparing': 'Preparing...',
  'issue.photoAlt': 'Your photo',
  'issue.photoRemove': 'Remove',
  'issue.photoNotImage': 'Not an image.',
  'issue.photoTooLarge': 'Photo too large.',
  'issue.photoUnreadable': 'Could not read that photo.',
  'issue.photoUploadFailed': 'Could not attach that photo.',
  // Names the one field that is missing. Items are optional - the report goes in
  // on the description alone, and the contract requires nothing else (GST-039).
  'issue.noText': 'Tell us what went wrong.',
  // SUP-006: support answers by email, so the address is required. The reason
  // is said out loud, because a guest who has just lost their breakfast is owed
  // one before being asked for another field.
  'issue.email': 'Your email',
  'issue.emailHint': 'So we can reply. We answer by email, not by text.',
  'issue.noEmail': 'Add an email so we can reply.',
  'issue.invalidEmail': 'That email does not look right.',
  'issue.wholeOrder': 'The whole order',

  // -------------------------------------------------------------------------
  // Receipt (PRD PAY-022)
  // -------------------------------------------------------------------------
  'receipt.tip': 'Courier tip',

  // -------------------------------------------------------------------------
  // Not-servable and paused (PRD GST-040)
  // -------------------------------------------------------------------------
  // Honest, and only about what actually happens: nothing on this screen records
  // the address, so it must not say it does (GST-040, WEB-004 deferred).
  'unavailable.contact':
    'We are opening new areas all the time. Your host can ask us about this one.',
  'unavailable.contactAction': 'Contact us',
  'unavailable.rescan': 'Scan a different code',
  /* Neither pre-order nor immediate delivery is switched on (OPS-025,
     GST-042). Not the same sentence as a pause, which is temporary and says
     so; orders already placed stay reachable behind this screen. */
  'storefront.closed.title': 'Ordering is closed for now',
  'unavailable.backHome': 'Back',

  // -------------------------------------------------------------------------
  // Errors and generic UI
  // -------------------------------------------------------------------------
  'error.title': 'Something went wrong',
  'error.requestId': 'Reference: {id}',
  'error.sessionExpired': 'This storefront session expired. Scan the code again to reopen it.',
  'error.rescan': 'Reopen the storefront',
  // -------------------------------------------------------------------------
  // Repeat schedules (PRD GST-010, section 4.3)
  // -------------------------------------------------------------------------

  'ui.yes': 'Yes',
  'ui.no': 'No',
  'ui.masked': 'Hidden',
  'ui.none': 'Not set',
  'ui.dismiss': 'Dismiss',
} as const;

export type GuestEnCatalogue = typeof en;
